import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import React from "react";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

const projectSpecUrl = "/crm/projects/:project_id/specs";

export const getProjectSpecs = createAsyncThunk(
  "ProjectSpec/getProjectSpecs",
  async ({projectId, params}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(projectSpecUrl.replace(":project_id", projectId), { params })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get project specs'))
    }
  }
)

export const createProjectSpec = createAsyncThunk(
  "ProjectSpec/createProjectSpec",
  async ({projectId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(
        projectSpecUrl.replace(":project_id", projectId),
        data, { headers: { 'Content-Type': 'multipart/form-data' }}
      )
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create project spec'))
    }
  }
)

export const updateProjectSpec = createAsyncThunk(
  "ProjectSpec/updateProjectSpec",
  async ({ projectId, specId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${projectSpecUrl.replace(":project_id", projectId)}/${specId}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update project spec'));
    }
  }
)

export const deleteProjectSpec = createAsyncThunk(
  "ProjectSpec/deleteProjectSpec",
  async ({projectId, specId}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${projectSpecUrl.replace(":project_id", projectId)}/${specId}`);
      return {
        specId,
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete project spec'));
    }
  }
)

export const removeTestingDocument = createAsyncThunk(
  "ProjectSpec/removeTestingDocument",
  async ({projectId, specId, document_id}, { rejectWithValue }) => {
    try {
      const url = `${projectSpecUrl.replace(":project_id", projectId)}/${specId}/remove_document`;
      const response = await apiAxios.post(url, { document_id });
      return response.data
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove testing document'))
    }
  }
)

export const getProjectSpecRevisions = createAsyncThunk(
  "ProjectSpec/getProjectSpecRevisions",
  async ({projectId, specId}, { rejectWithValue }) => {
    try {
      const url = `${projectSpecUrl.replace(":project_id", projectId)}/${specId}/spec_revisions`;
      const response = await apiAxios.get(url);
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get project spec revisions'))
    }
  }
)

export const createProjectSpecRevision = createAsyncThunk(
  "ProjectSpec/createProjectSpecRevision",
  async ({projectId, specId, data}, { rejectWithValue }) => {
    try {
      const url = `${projectSpecUrl.replace(":project_id", projectId)}/${specId}/spec_revisions`;
      const response = await apiAxios.post(
        url,
        data, { headers: { 'Content-Type': 'multipart/form-data' }}
      )
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create project spec revision'))
    }
  }
)

export const updateProjectSpecRevision = createAsyncThunk(
  "ProjectSpec/updateProjectSpecRevision",
  async ({ projectId, specId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = `${projectSpecUrl.replace(":project_id", projectId)}/${specId}/spec_revisions/${revisionId}`;
      const response = await apiAxios.put(url, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update project spec revision'));
    }
  }
)

export const deleteProjectSpecRevision = createAsyncThunk(
  "ProjectSpec/deleteProjectSpecRevision",
  async ({projectId, specId, revisionId}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${projectSpecUrl.replace(":project_id", projectId)}/${specId}/spec_revisions/${revisionId}`);
      return {
        specId,
        revisionId,
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete project spec revision'));
    }
  }
)

export const removeRevisionDocument = createAsyncThunk(
  "ProjectSpec/removeRevisionDocument",
  async ({projectId, specId, revisionId, document_id}, { rejectWithValue }) => {
    try {
      const url = `${projectSpecUrl.replace(":project_id", projectId)}/${specId}/spec_revisions/${revisionId}/remove_document`;
      const response = await apiAxios.post(url, { document_id });
      return response.data
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove revision document'))
    }
  }
)

const handlePayload = (state, action, type="none") => {
  const { error, data, message } = action.payload;

  if (error !== undefined) {
    toast.error(error);
    return;
  } 
  else 
  {
    const record = data.attributes
    
    if (record !== null) {
      switch (type) {
        case "create": 
          state.data.unshift(record)
          break;
        case "delete":
          state.data = state.data.filter((item) => item.id !== record.id)
          break;
        default:
        {
          const updatedIndex = state.data.findIndex((item) => item.id === record.id)
          if (updatedIndex !== -1) {
            state.data[updatedIndex] = record
          }
          break;
        }
      }
    }

    if (message !== null) {
      toast.success(<span dangerouslySetInnerHTML={{ __html: message }} />);
    }
  }
};

const handlePayloadRevision = (state, action, type="none") => {
  const { error, data, message } = action.payload;

  if (error !== undefined) {
    toast.error(error);
    return;
  } 
  else 
  {
    const record = data.attributes
    if (record !== null) {
      const specId = record.spec_id;
      const updatedIndex = state.data.findIndex((item) => item.id === specId)
      const newData = state.data[updatedIndex];

      if (type === "delete") {
        newData.lastest_revision = record;
      }
      else {
        newData.lastest_revision = null;
      }

      // switch (type) {
      //   case "create": 
      //     newData.revisions.unshift(record)
      //     break;
      //   case "delete":
      //     newData.revisions = newData.revisions.filter((item) => item.id !== record.id)
      //     break;
      //   default:
      //   {``
      //     const updatedIndex = newData.revisions.findIndex((item) => item.id === record.id)
      //     if (updatedIndex !== -1) {
      //       newData.revisions[updatedIndex] = record
      //     }
      //     break;
      //   }
      // }
    }

    if (message !== null) {
      toast.success(<span dangerouslySetInnerHTML={{ __html: message }} />);
    }
  }
};


const specSlice = createSlice({
  name: "crm_project_spec",
  initialState: {
    data: []
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectSpecs.fulfilled, (state, action) => {
        const data = action.payload;
        state.data = Array.isArray(data) ? data.map(item => item.attributes) : [];
      })
      .addCase(createProjectSpec.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(updateProjectSpec.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(removeTestingDocument.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deleteProjectSpec.fulfilled, (state, action) => {
        const { specId, data } = action.payload;

        if (data.error) {
          toast.error(data.error);
        } else {
          state.data = state.data.filter((item) => item.id !== specId)
          toast.success(`Ref no ${specId} was successfully deleted.`);
        }
      })
      .addCase(createProjectSpecRevision.fulfilled, (state, action) => {
        handlePayloadRevision(state, action)
      })
      .addCase(updateProjectSpecRevision.fulfilled, (state, action) => {
        handlePayloadRevision(state, action)
      })
      .addCase(deleteProjectSpecRevision.fulfilled, (state, action) => {
        handlePayloadRevision(state, action, "delete")
      })

      // Add standard rejected action matcher using shared helper
      addRejectedMatcher(builder, "ProjectSpec");
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = specSlice.actions

export default specSlice.reducer